import React from 'react';
import Section from '../layouts/Section';
import PageLayout from '../layouts/PageLayout';

const PrivacyPolicy = () => {
  return (
    <PageLayout title="Cell Station – Privacy Policy">
      <Section title="Privacy Policy">
        <div className="section">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div className="policy-content" style={styles.content}>
                  <p style={styles.lastUpdated}>Last Updated: January 2025</p>

                  <div style={styles.introBox}>
                    <p style={styles.introParagraph}>
                      At Cell Station, we are committed to protecting your privacy and ensuring the
                      security of your personal information. This Privacy Policy explains how we collect,
                      use, disclose, and safeguard your information when you visit our store or use our services.
                    </p>
                  </div>

                  <div style={styles.section}>
                    <h3 style={styles.heading}>1. Information We Collect</h3>

                    <h4 style={styles.subheading}>Personal Information</h4>
                    <p style={styles.paragraph}>
                      When you use our services, we may collect the following information:
                    </p>
                    <ul style={styles.list}>
                      <li>Name and contact information (phone number, email address)</li>
                      <li>Device information (make, model, serial number, IMEI)</li>
                      <li>Service history and repair records</li>
                      <li>Payment information (processed securely through our payment providers)</li>
                      <li>Communication preferences</li>
                    </ul>

                    <h4 style={styles.subheading}>Device Data</h4>
                    <p style={styles.paragraph}>
                      During repairs, our technicians may need to access your device to diagnose and fix issues.
                      We do not access, copy, or store your personal data (photos, messages, contacts, etc.)
                      unless explicitly required for a data recovery service you have requested.
                    </p>
                  </div>

                  <div style={styles.section}>
                    <h3 style={styles.heading}>2. How We Use Your Information</h3>
                    <p style={styles.paragraph}>
                      We use the information we collect to:
                    </p>
                    <ul style={styles.list}>
                      <li>Process and complete your repair orders</li>
                      <li>Contact you regarding your device status and pickup</li>
                      <li>Provide warranty services and support</li>
                      <li>Send promotional offers and updates (with your consent)</li>
                      <li>Improve our services and customer experience</li>
                      <li>Comply with legal obligations</li>
                    </ul>
                  </div>

                  <div style={styles.section}>
                    <h3 style={styles.heading}>3. Information Sharing</h3>
                    <p style={styles.paragraph}>
                      We do not sell, trade, or rent your personal information to third parties. We may share
                      your information only in the following circumstances:
                    </p>
                    <ul style={styles.list}>
                      <li><strong>Service Providers:</strong> Payment processors and shipping partners who assist in our operations</li>
                      <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                      <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                    </ul>
                  </div>

                  <div style={styles.section}>
                    <h3 style={styles.heading}>4. Data Security</h3>
                    <p style={styles.paragraph}>
                      We implement appropriate technical and organizational security measures to protect
                      your personal information against unauthorized access, alteration, disclosure, or destruction.
                      These measures include:
                    </p>
                    <ul style={styles.list}>
                      <li>Secure storage of customer records</li>
                      <li>Limited employee access to personal information</li>
                      <li>Encrypted payment processing</li>
                      <li>Regular security assessments</li>
                    </ul>
                  </div>

                  <div style={styles.section}>
                    <h3 style={styles.heading}>5. Data Retention</h3>
                    <p style={styles.paragraph}>
                      We retain your personal information for as long as necessary to fulfill the purposes
                      outlined in this policy, unless a longer retention period is required by law. Service
                      records are typically retained for warranty and legal compliance purposes.
                    </p>
                  </div>

                  <div style={styles.section}>
                    <h3 style={styles.heading}>6. Your Rights</h3>
                    <p style={styles.paragraph}>
                      You have the right to:
                    </p>
                    <ul style={styles.list}>
                      <li>Access the personal information we hold about you</li>
                      <li>Request correction of inaccurate information</li>
                      <li>Request deletion of your information (subject to legal requirements)</li>
                      <li>Opt out of marketing communications</li>
                      <li>Lodge a complaint with a supervisory authority</li>
                    </ul>
                  </div>

                  <div style={styles.section}>
                    <h3 style={styles.heading}>7. Cookies and Website Tracking</h3>
                    <p style={styles.paragraph}>
                      Our website may use cookies and similar tracking technologies to enhance your
                      browsing experience. You can control cookie preferences through your browser settings.
                    </p>
                  </div>

                  <div style={styles.section}>
                    <h3 style={styles.heading}>8. Children's Privacy</h3>
                    <p style={styles.paragraph}>
                      Our services are not directed to individuals under the age of 13. We do not knowingly
                      collect personal information from children under 13. If we become aware that we have
                      collected such information, we will take steps to delete it promptly.
                    </p>
                  </div>

                  <div style={styles.section}>
                    <h3 style={styles.heading}>9. Changes to This Policy</h3>
                    <p style={styles.paragraph}>
                      We may update this Privacy Policy from time to time. Any changes will be posted on
                      this page with an updated revision date. We encourage you to review this policy
                      periodically for any changes.
                    </p>
                  </div>

                  <div style={styles.contactBox}>
                    <h4 style={styles.contactTitle}>Privacy Concerns or Questions?</h4>
                    <p style={styles.contactText}>
                      If you have any questions about this Privacy Policy or wish to exercise your rights,
                      please contact us:
                    </p>
                    <p style={styles.contactText}>
                      <i className="fas fa-envelope" style={styles.icon}></i>
                      <a href="mailto:icellstation1@gmail.com" style={styles.link}>icellstation1@gmail.com</a>
                    </p>
                    <p style={styles.contactText}>
                      <i className="fas fa-phone" style={styles.icon}></i>
                      <a href="tel:+12159393933" style={styles.link}>+1 (215) 939-3933</a>
                    </p>
                    <p style={styles.contactText}>
                      <i className="fas fa-map-marker-alt" style={styles.icon}></i>
                      Cell Station, 2500 W Moreland Rd, Space #1077, Willow Grove, PA 19090
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </PageLayout>
  );
};

const styles = {
  content: {
    backgroundColor: '#fff',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 5px 30px rgba(0,0,0,0.08)',
    marginBottom: '40px',
  },
  lastUpdated: {
    color: '#666',
    fontStyle: 'italic',
    marginBottom: '20px',
  },
  introBox: {
    backgroundColor: '#f0f7ff',
    padding: '25px',
    borderRadius: '10px',
    marginBottom: '30px',
    borderLeft: '4px solid #0066cc',
  },
  introParagraph: {
    color: '#333',
    lineHeight: '1.8',
    margin: 0,
  },
  section: {
    marginBottom: '30px',
  },
  heading: {
    color: '#1a1a2e',
    fontSize: '22px',
    fontWeight: '600',
    marginBottom: '15px',
  },
  subheading: {
    color: '#333',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '10px',
    marginTop: '20px',
  },
  paragraph: {
    color: '#555',
    lineHeight: '1.8',
    marginBottom: '15px',
  },
  list: {
    color: '#555',
    lineHeight: '2',
    paddingLeft: '20px',
  },
  contactBox: {
    backgroundColor: '#f8f9fa',
    padding: '30px',
    borderRadius: '10px',
    borderLeft: '4px solid #F50963',
    marginTop: '40px',
  },
  contactTitle: {
    color: '#1a1a2e',
    marginBottom: '15px',
  },
  contactText: {
    color: '#555',
    marginBottom: '10px',
    lineHeight: '1.8',
  },
  link: {
    color: '#F50963',
    textDecoration: 'none',
  },
  icon: {
    color: '#F50963',
    marginRight: '10px',
    width: '20px',
  },
};

export default PrivacyPolicy;
